<template>
	<div class="tooltip-c" ref="trigger" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
		<IconInfo v-if="type_prop == '1'" class="" :width_prop="iconWidth_prop" :height_prop="iconHeight_prop"
			:color_prop="iconColor_prop" />
		<IconInfo2 v-if="type_prop == '2'" class="" :width_prop="iconWidth_prop" :height_prop="iconHeight_prop"
			:color_prop="iconColor_prop" />
		<IconInfo3 v-if="type_prop == '3'" class="" :width_prop="iconWidth_prop" :height_prop="iconHeight_prop"
			:color_prop="iconColor_prop" />

		<transition name="fade">
			<div v-if="visible" ref="tooltip" :class="`tooltiptext-c ${textAlign} ${maxWidth_prop} ${tooltipClass_prop}`"
				@mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
				<slot v-if="$slots.default"></slot>
				<span v-else>{{ text_prop }}</span>
			</div>
		</transition>
	</div>
</template>

<script>
import IconInfo from '@/components/icons/IconInfo.vue'
import IconInfo2 from './icons/IconInfo2.vue'
import IconInfo3 from './icons/IconInfo3.vue'

export default {
	name: 'Tooltip',
	components: { IconInfo, IconInfo2, IconInfo3 },

	props: {
		type_prop: { type: String, default: '1' },
		text_prop: { type: String, default: '' },
		textAlign_prop: { type: String, default: 'center' },
		maxWidth_prop: { type: String, default: 'max-w-75' },
		tooltipClass_prop: { type: String, default: '' },
		iconWidth_prop: { type: String, default: '1rem' },
		iconHeight_prop: { type: String, default: '1rem' },
		iconColor_prop: { type: String, default: getComputedStyle(document.documentElement).getPropertyValue('--color-theme') }
	},

	data() {
		return {
			visible: false,
			hideTimeout: null,
		}
	},

	computed: {
		textAlign() {
			const map = { left: 'text-left', center: 'text-center', right: 'text-right' }
			return map[this.textAlign_prop] || 'text-center'
		},
	},

	methods: {
		handleMouseEnter() {
			clearTimeout(this.hideTimeout)
			if (!this.visible) this.show()
		},

		handleMouseLeave() {
			clearTimeout(this.hideTimeout)
			// small delay to allow movement between trigger and tooltip
			this.hideTimeout = setTimeout(() => {
				this.hide()
			}, 150)
		},

		show() {
			this.visible = true
			this.$nextTick(() => {
				const tooltip = this.$refs.tooltip
				const trigger = this.$refs.trigger
				if (!tooltip || !trigger) return

				document.body.appendChild(tooltip)

				const rect = trigger.getBoundingClientRect()
				const tooltipRect = tooltip.getBoundingClientRect()
				const gap = 8
				const scrollY = window.scrollY
				const scrollX = window.scrollX
				const screenW = window.innerWidth
				const screenH = window.innerHeight

				let top = rect.bottom + scrollY + gap
				let left = rect.left + scrollX + rect.width / 2 - tooltipRect.width / 2

				// vertical auto-adjust
				if (top + tooltipRect.height > scrollY + screenH - gap)
					top = rect.top + scrollY - tooltipRect.height - gap
				if (top < scrollY + gap) top = scrollY + gap

				// horizontal auto-adjust
				if (left + tooltipRect.width > scrollX + screenW - gap)
					left = scrollX + screenW - tooltipRect.width - gap
				if (left < scrollX + gap) left = scrollX + gap

				tooltip.style.position = 'absolute'
				tooltip.style.top = `${top}px`
				tooltip.style.left = `${left}px`
				tooltip.style.zIndex = 9999
				tooltip.style.opacity = '1'
				tooltip.style.visibility = 'visible'
				tooltip.style.pointerEvents = 'auto' // ✅ allow hover events now
			})
		},

		hide() {
			this.visible = false
		},
	},

	destroyed() {
		const tooltip = this.$refs.tooltip
		if (tooltip && tooltip.parentNode === document.body) {
			tooltip.parentNode.removeChild(tooltip)
		}
	},
}
</script>

<style scoped>
.tooltip-c {
	position: relative;
	display: inline-block;
	cursor: pointer;
}

.tooltiptext-c {
	position: absolute;
	background-color: var(--color-grey12);
	color: #fff;
	border-radius: 0.25rem;
	padding: 0.75rem;
	font-weight: 600;
	box-shadow: 0 0 0.625rem -0.1875rem rgba(0, 0, 0, 0.5);
	opacity: 0;
	visibility: hidden;
	transition: opacity 0.15s ease, visibility 0.15s ease;

	/* ✅ enable interaction for hover */
	pointer-events: auto;

	min-width: 8rem;
	max-width: 25ch;
	white-space: normal;
	word-break: break-word;
	line-height: 1.3;
	text-align: center;
}

/* fade animation */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.15s ease;
}
.fade-enter,
.fade-leave-to {
	opacity: 0;
}
</style>
